import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomersMergeRequestBody, MergePreview } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { getApiClientErrorStatus } from '@/services/api/errors';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { customer, customerKeys, type Customer } from './entity';
import { getCustomer, getCustomersByEmail, getPeople } from './queries';

/**
 * Merging two profiles: the preview (what the merged profile would hold), the
 * commit, and the profile search the merge page selects its pair with.
 *
 * Both the preview and the commit take the generated `CustomersMergeRequestBody`:
 * exactly two `customer_ids` (internal `cio_id`s) with `primary_customer_id` one
 * of them. The primary keeps every value it has; the secondary contributes only
 * the attributes the primary lacks.
 */

/** The two profiles a merge acts on; the secondary is folded into the primary. */
export interface MergePair {
  primaryCustomerId: string;
  secondaryCustomerId: string;
}

/** The wire body for a merge (or its preview) of `pair`. */
export function mergeRequestBody({ primaryCustomerId, secondaryCustomerId }: MergePair): CustomersMergeRequestBody {
  return { customer_ids: [primaryCustomerId, secondaryCustomerId], primary_customer_id: primaryCustomerId };
}

export interface MergeCustomersInput extends CustomersMergeRequestBody {
  workspaceId: number;
}

/** @public The projected merged attribute set, without performing the merge. */
export async function previewCustomerMerge({ workspaceId, ...body }: MergeCustomersInput): Promise<MergePreview> {
  return hydraUiApi.customers.mergePreview({
    params: { path: { environment_id: String(workspaceId) } },
    body,
  });
}

/**
 * Merge the secondary into the primary. The backend performs the merge
 * asynchronously, so the merged profile settles a few seconds later; the
 * operation cannot be undone.
 *
 * @public
 */
export async function mergeCustomers({ workspaceId, ...body }: MergeCustomersInput): Promise<void> {
  await hydraUiApi.customers.merge({
    params: { path: { environment_id: String(workspaceId) } },
    body,
  });
}

/**
 * Whether the workspace keeps a global unsubscribe through a merge. Read fresh
 * each time (never cached) and best-effort: an unreadable setting falls back to
 * the default, off.
 *
 * @public
 */
export async function getPreserveUnsubscribesOnMerge(workspaceId: number): Promise<boolean> {
  try {
    const { enabled } = await hydraUiApi.workspaceSettings.getPreserveUnsubscribesOnMerge({
      params: { path: { environment_id: String(workspaceId) } },
    });
    return enabled;
  } catch {
    return false;
  }
}

const UNSUBSCRIBED_ATTRIBUTE = 'unsubscribed';

export interface PreservedUnsubscribeContext {
  preserveUnsubscribesOnMerge: boolean;
  primaryUnsubscribed: boolean;
  secondaryUnsubscribed: boolean;
  secondaryCioId: string;
}

/**
 * The preview endpoint always lets the primary's subscription state win, so a
 * secondary-only global unsubscribe is missing from the preview. With the
 * workspace's preserve-unsubscribes-on-merge setting it survives the merge, so
 * carry it over: the merged profile keeps `unsubscribed = true`, attributed to
 * the secondary (both cards then show it as carried over) and counted as an
 * added attribute. When the primary is already unsubscribed the preview is
 * right as it is.
 *
 * @public
 */
export function applyPreservedUnsubscribe(
  preview: MergePreview,
  {
    preserveUnsubscribesOnMerge,
    primaryUnsubscribed,
    secondaryUnsubscribed,
    secondaryCioId,
  }: PreservedUnsubscribeContext,
): MergePreview {
  if (!preserveUnsubscribesOnMerge || primaryUnsubscribed || !secondaryUnsubscribed) {
    return preview;
  }

  const attributes = [
    ...preview.attributes.filter((attribute) => attribute.name !== UNSUBSCRIBED_ATTRIBUTE),
    { cio_id: secondaryCioId, name: UNSUBSCRIBED_ATTRIBUTE, value: 'true' },
  ];
  return {
    ...preview,
    attributes,
    summary: {
      ...preview.summary,
      attributes: {
        ...preview.summary.attributes,
        added: (preview.summary.attributes.added ?? 0) + 1,
        merged: attributes.length,
      },
    },
  };
}

export interface MergePreviewInput extends MergePair {
  workspaceId: number;
  /** The pair's current global-unsubscribe states, which decide whether an unsubscribe is carried over. */
  primaryUnsubscribed: boolean;
  secondaryUnsubscribed: boolean;
  /** The secondary's internal id, which an added carried-over attribute is attributed to. */
  secondaryCioId: string;
}

/**
 * The merge preview as the page shows it: the endpoint's projection with the
 * workspace's preserve-unsubscribes setting applied.
 *
 * @public
 */
export async function getMergePreview({
  workspaceId,
  primaryCustomerId,
  secondaryCustomerId,
  ...context
}: MergePreviewInput): Promise<MergePreview> {
  const [preview, preserveUnsubscribesOnMerge] = await Promise.all([
    previewCustomerMerge({ workspaceId, ...mergeRequestBody({ primaryCustomerId, secondaryCustomerId }) }),
    getPreserveUnsubscribesOnMerge(workspaceId),
  ]);
  return applyPreservedUnsubscribe(preview, { preserveUnsubscribesOnMerge, ...context });
}

type MergePreviewVariables = Omit<MergePreviewInput, 'workspaceId'>;

/**
 * Non-suspense preview read for a pair; `null` while no valid pair is selected.
 * Never cached: the preview is a projection over live profiles and a workspace
 * setting, so every (re)selection asks again. The key carries only the pair —
 * the unsubscribe context is derived from those same two profiles.
 *
 * @public
 */
export const useMergePreviewOptional = ({
  workspaceId: overrideWorkspaceId,
  pair,
}: {
  workspaceId?: number;
  pair: MergePreviewVariables | null;
}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  return useQuery({
    queryKey:
      pair != null
        ? customerKeys.mergePreview(workspaceId, pair.primaryCustomerId, pair.secondaryCustomerId)
        : ['customer', 'merge-preview', 'inactive'],
    queryFn: pair != null ? () => getMergePreview({ workspaceId, ...pair }) : skipToken,
    staleTime: 0,
    gcTime: 0,
  });
};

type MergeVariables = CustomersMergeRequestBody;

/**
 * Commit a merge. A merge removes a row from the list and changes the primary's
 * attributes, so the workspace's customer reads are invalidated on success.
 *
 * @public
 */
export const useMergeCustomers = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: { workspaceId?: number; mutationConfig?: HookMutationConfig<void, MergeVariables> } = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: MergeVariables) => mergeCustomers({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...callbackArgs) => {
      void qc.invalidateQueries({ queryKey: customerKeys.all(workspaceId) });
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* Profile search                                                             */
/* ------------------------------------------------------------------------- */

/**
 * How the merge page looks a profile up: by any identifier value (`id`), by
 * exact email, or by an encoded condition filter.
 */
export type CustomerSearchQuery = { id: string } | { email: string } | { filters: string };

/**
 * The profiles matching `query`. An `id` lookup resolves any identifier the
 * backend recognizes and yields no match (not an error) on 404.
 *
 * @public
 */
export async function searchCustomers(workspaceId: number, query: CustomerSearchQuery): Promise<Customer[]> {
  if ('id' in query) {
    try {
      return [await getCustomer(workspaceId, query.id.trim())];
    } catch (error) {
      if (getApiClientErrorStatus(error) === 404) {
        return [];
      }
      throw error;
    }
  }
  if ('email' in query) {
    return getCustomersByEmail(workspaceId, query.email.trim());
  }
  const response = await getPeople(workspaceId, { filters: query.filters });
  return response.customers.map((raw) => customer.parse(raw));
}
